import ToolbarDensitySelector from '@/global/components/DataGrid/components/CustomGridToolBar/DensitySelector'
import {
    GridButton,
    GridMenuButton,
    GridMenuItem,
} from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { cn } from '@/lib/utils'
import { separateWords } from '@acme/auth'
import { useAuth } from 'hooks'
import {
    Calendar1Icon,
    CalendarClockIcon,
    FileSpreadsheetIcon,
    LibraryBigIcon,
    ListFilterIcon,
} from 'lucide-react'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { jornada, JornadaDiariaQueryXor } from 'service/api'
import { useDigitacaoColumns } from '../columns'
import { useDigitacaoContext } from '../pages/useDigitacaoContext'
import { useDigitacaoStore } from '../utils/store'

const toolbarUrl = [
    '/manutencao-ponto/digitacao',
    '/manutencao-ponto/digitacao/centro-custo',
    '/manutencao-ponto/digitacao/locais',
]

interface DigitacaoToolbarProps {
    children?: React.ReactNode
}

const DigitacaoToolbar: React.FC<DigitacaoToolbarProps> = ({ children }) => {
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const [value, setValue] = React.useState(() => {
        const defaultIndex = toolbarUrl.indexOf(pathname)
        return defaultIndex === -1 ? 0 : defaultIndex
    })
    const { decoded } = useAuth()
    const { view } = useDigitacaoContext()
    const [organizar, setOrganizar] = useDigitacaoStore(state => [
        state.organizacao,
        state.setOrganizar,
    ])

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        setValue(newValue)
        setTimeout(() => {
            navigate(toolbarUrl[newValue])
        }, 100)
    }

    return (
        <div
            className={cn(
                'border-x flex justify-between items-center gap-3 pb-0.5 px-2',
                'overflow-x-auto scrollbar-thin! min-h-15',
            )}
        >
            <div className="inline-flex gap-2">
                {decoded.categoria !== 5 && (
                    <GridMenuButton
                        startIcon={<ListFilterIcon />}
                        label="Filtrar"
                    >
                        <GridMenuItem
                            onClick={() => handleChange({} as any, 0)}
                            label="Por funcionário"
                            startIcon={Circle(value === 0)}
                        />
                        <GridMenuItem
                            onClick={() => handleChange({} as any, 1)}
                            label="Por centro de custo"
                            startIcon={Circle(value === 1)}
                        />
                        <GridMenuItem
                            onClick={() => handleChange({} as any, 2)}
                            label="Por local"
                            startIcon={Circle(value === 2)}
                        />
                    </GridMenuButton>
                )}
                <ToolbarDensitySelector />
                {view === 'mensal' && (
                    <GridMenuButton
                        startIcon={<LibraryBigIcon />}
                        label="Organizar"
                    >
                        <GridMenuItem
                            startIcon={
                                <Calendar1Icon className="size-5 mr-2" />
                            }
                            onClick={() => setOrganizar('dia')}
                            label="Por dia"
                            selected={organizar === 'dia'}
                        />
                        <GridMenuItem
                            startIcon={
                                <CalendarClockIcon className="size-5 mr-2" />
                            }
                            onClick={() => setOrganizar('jornada')}
                            label="Por jornada"
                            selected={organizar === 'jornada'}
                        />
                    </GridMenuButton>
                )}
                {view === 'diario' && <ExportarCsvAssinado />}
            </div>
            <div className="flex gap-2">{children}</div>
        </div>
    )
}

function Circle(checked?: boolean) {
    return (
        <div className="rounded-full size-5 ring-2 ring-primary flex items-center justify-center mr-2">
            <div
                className={`rounded-full size-3 ${
                    checked ? 'bg-primary' : 'bg-transparent'
                }`}
            />
        </div>
    )
}

function ExportarCsvAssinado() {
    const { queryParams } = useDigitacaoContext()
    const columns = useDigitacaoColumns()
    const gridData = jornada.diaria.getQueryData(queryParams as any) ?? []

    const handleExport = () => {
        const maximoRegistros = Math.max(
            gridData
                .sort(
                    (a: any, b: any) =>
                        b.registros?.length - a.registros?.length,
                )
                .at(0)?.registros?.length || 0,
            2,
        )

        function getCellValue(id: any, field: any) {
            const rowData = gridData.find((item: any) => item.id === id)
            if (!rowData) return ''
            let value = (rowData as any)[field]
            const col = columns.find(column => column.field === field)
            if (field === 'registros') {
                const horarios = rowData.registros.map(
                    registro => registro.hora,
                )
                if (horarios.length < maximoRegistros)
                    horarios.push(
                        ...Array(maximoRegistros - horarios.length).fill(''),
                    )
                return horarios.map(it => `"${it || ''}"`).join(';')
            }
            if (col?.valueGetter)
                value = col.valueGetter({
                    row: rowData,
                    value,
                    field,
                    id,
                } as any)
            if (field === 'ausencias') {
                const ausencias = Object.entries(value ?? {})
                    .filter(([_, v]) => !!v)
                    .map(([k]) => separateWords(k))
                    .join(', ')
                return `"${ausencias}"` as any
            }
            if (col?.valueFormatter)
                value = (() => {
                    try {
                        return col.valueFormatter({
                            value,
                            row: rowData,
                            field,
                            id,
                        } as any)
                    } catch (_) {
                        return value
                    }
                })()
            if (value === null || value === undefined) return ''

            return `"${value}"` as any
        }

        const exportPromise = new Promise((resolve, reject) => {
            try {
                const headers = columns
                    .filter(column => !column.disableExport)
                    .map(({ headerName, field }) => [headerName, field])
                const csvContent = [
                    headers
                        .map(([header]) => `${header}`)
                        .join(';')
                        .replace(
                            'Registros',
                            Array.from({ length: maximoRegistros })
                                .map((_, index) =>
                                    index % 2 === 0 ? 'Entrada' : 'Saída',
                                )
                                .join(';'),
                        ),
                    ...gridData.map(({ id }) =>
                        headers
                            .map(([, field]) => getCellValue(id, field))
                            .join(';'),
                    ),
                ].join('\n')

                const csvDataUri = `data:text/csv;charset=utf-8,${encodeURIComponent(
                    csvContent,
                )}`
                const link = document.createElement('a')
                link.setAttribute('href', csvDataUri)
                link.setAttribute(
                    'download',
                    `exportacao-digitacao-diaria-${
                        (queryParams as JornadaDiariaQueryXor).dia
                    }`,
                )
                link.click()

                resolve(true)
            } catch (error) {
                reject(error)
            }
        })

        return exportPromise
    }

    return (
        <GridButton
            onClick={handleExport}
            startIcon={<FileSpreadsheetIcon />}
            label="Exportar CSV"
        />
    )
}

export default DigitacaoToolbar
